import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Database, Download, Upload } from "lucide-react";
import { DataEntityId, ENTITY_CONFIGS } from "./types";
import { EntityImportPanel } from "./EntityImportPanel";
import { AccountExportPanel } from "./AccountExportPanel";
import { ClientExportPanel } from "./ClientExportPanel";
import { SupplierExportPanel } from "./SupplierExportPanel";
import { streamingAccountService } from "@/services/streaming-account";
import { clientService } from "@/services/client";
import { supplierService } from "@/services/supplier";

const EXPORT_PANELS: Record<DataEntityId, React.ComponentType<{ entity: (typeof ENTITY_CONFIGS)[number] }>> = {
  clients: ClientExportPanel,
  accounts: AccountExportPanel,
  suppliers: SupplierExportPanel,
};

const IMPORT_HANDLERS: Record<
  DataEntityId,
  (params: { file: string; updated: boolean }) => ReturnType<typeof streamingAccountService.importAccounts>
> = {
  clients: (params) => clientService.importClients(params),
  accounts: (params) => streamingAccountService.importAccounts(params),
  suppliers: (params) => supplierService.importSuppliers(params),
};

export function DataImportExportCard() {
  const [entityId, setEntityId] = useState<DataEntityId>(ENTITY_CONFIGS[0].id);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Database className="w-4 h-4 text-primary" />
          Datos
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Importa o exporta la información de tu cuenta por entidad.
        </p>
      </CardHeader>
      <CardContent>
        <Tabs value={entityId} onValueChange={(v) => setEntityId(v as DataEntityId)}>
          <TabsList>
            {ENTITY_CONFIGS.map((e) => (
              <TabsTrigger key={e.id} value={e.id}>
                {e.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {ENTITY_CONFIGS.map((e) => {
            const ExportPanelComponent = EXPORT_PANELS[e.id];
            return (
              <TabsContent key={e.id} value={e.id} className="space-y-4">
                <p className="text-xs text-muted-foreground">{e.description}</p>

                <Tabs defaultValue="import" className="w-full">
                  <TabsList>
                    <TabsTrigger value="import" className="gap-1.5">
                      <Upload className="w-3.5 h-3.5" />
                      Importar
                    </TabsTrigger>
                    <TabsTrigger value="export" className="gap-1.5">
                      <Download className="w-3.5 h-3.5" />
                      Exportar
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="import" className="pt-4">
                    <EntityImportPanel key={e.id} entity={e} onImport={IMPORT_HANDLERS[e.id]} />
                  </TabsContent>
                  <TabsContent value="export" className="pt-4">
                    <ExportPanelComponent key={e.id} entity={e} />
                  </TabsContent>
                </Tabs>
              </TabsContent>
            );
          })}
        </Tabs>
      </CardContent>
    </Card>
  );
}
